import React, { useState } from "react";
import { ChevronRight, CopyPlus, Github, ListChecks, Repeat, SlidersHorizontal, Wand2, Zap } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../ui/sheet";
import { Switch } from "../ui/switch";
import ModelSelect from "../ModelSelect";
import { useCredits } from "../../context/CreditsContext";

export const FAST_MODEL = "claude-haiku-5-5";

const Toggle = ({ icon: Icon, title, desc, checked, onChange, disabled, testid }) => (
  <label className={`flex items-center gap-3 px-4 py-3.5 ${disabled ? "opacity-50" : "cursor-pointer hover:bg-slate-50"} transition-colors`}>
    <Icon size={18} className="text-slate-700 shrink-0" />
    <span className="flex-1 min-w-0"><span className="block text-[15px] text-slate-900">{title}</span>
      {desc && <span className="block text-xs text-slate-500 leading-snug">{desc}</span>}</span>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} className="data-[state=checked]:bg-sky-500" />
  </label>
);

const Action = ({ icon: Icon, title, onClick, disabled, testid }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-50 disabled:opacity-40 transition-colors">
    <Icon size={18} className="text-slate-700 shrink-0" /><span className="flex-1 text-[15px] text-slate-900">{title}</span>
    <ChevronRight size={16} className="text-slate-400" />
  </button>
);

const Group = ({ children }) => <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">{children}</div>;

export const SettingsSheet = ({ s, testid = "chat-settings" }) => {
  const [open, setOpen] = useState(false);
  const { pricing } = useCredits();
  const active = s.plan || s.ai || s.fast;
  const go = (fn) => { setOpen(false); fn(); };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid} aria-label="Settings" title="Settings"
        className={`relative w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-colors ${active ? "text-sky-600 bg-sky-50" : "text-slate-600 hover:bg-slate-100"}`}>
        <SlidersHorizontal size={18} strokeWidth={1.8} />
        {active && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-sky-500" />}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl bg-slate-50 border-slate-200 p-4 pb-8 max-h-[88vh] overflow-y-auto sm:max-w-lg sm:mx-auto" data-testid={`${testid}-sheet`}>
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-300" />
          <SheetTitle className="px-1 text-lg text-slate-900">Settings</SheetTitle>
          <SheetDescription className="px-1 -mt-2 mb-2 text-xs text-slate-500">Applies to your next message in this project.</SheetDescription>
          <div className="space-y-3">
            <Group>
              <div className="flex items-center gap-3 px-4 py-2.5">
                <span className="flex-1 text-[15px] text-slate-900">Model</span>
                <ModelSelect value={s.fast ? FAST_MODEL : s.model} onChange={(m) => { s.setModel(m); s.setFast(false); }} testid={`${testid}-model`} align="end" />
              </div>
              <p className="px-4 pb-3 -mt-1 text-xs text-slate-500" data-testid={`${testid}-continue-note`}>If a big build runs out of room, it continues automatically — each extra part costs the model's price again.</p>
            </Group>
            <Group>
              <Toggle icon={ListChecks} title="Plan mode" desc={`Shares a plan and asks questions before coding · ${pricing.plan} credit`} checked={s.plan} onChange={s.togglePlan} testid={`${testid}-plan-switch`} />
              <Toggle icon={Repeat} title="Always plan first" desc="Every message in this project starts with a plan" checked={!!s.always} onChange={s.toggleAlways} testid={`${testid}-always-switch`} />
              <Toggle icon={Zap} title="Fast mode" desc="Quickest model for small tweaks (Claude Haiku 5.5)" checked={s.fast} onChange={s.setFast} testid={`${testid}-fast-switch`} />
              <Toggle icon={Wand2} title="AI images" desc={`Up to 3 custom images · up to ${pricing.images} credits, unused refunded`} checked={s.ai && !s.plan} onChange={s.setAi} disabled={s.plan} testid={`${testid}-ai-images-switch`} />
            </Group>
            <Group>
              <Action icon={Github} title="Save to GitHub" onClick={() => go(s.onGithub)} disabled={!s.hasVersion} testid={`${testid}-github`} />
              <Action icon={CopyPlus} title="Fork" onClick={() => go(s.onFork)} disabled={!s.hasVersion} testid={`${testid}-fork`} />
            </Group>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default SettingsSheet;
